/**
 * narration — Phase 25 Increment 5.
 *
 * Client for the Worker's narration + voice-clone endpoints, reached through
 * the same-origin BFF proxy (`/api/*` → Cloudflare Worker → FastAPI core).
 *
 * Before this the NarrationPanel "나레이션 적용" button only bubbled a config
 * object — `/narration/synthesize` was never called. The Worker routes
 * (`workers/edge/src/routes/narration.ts`, `voice_clone.ts`) were fully
 * implemented but orphaned. This module connects them.
 *
 * All calls are auth-gated on the Worker (userId + credits). A 401 means the
 * user must log in; a 402 means insufficient credits — callers surface those.
 */

export interface NarrationResult<T> {
	ok: boolean;
	status: number;
	data?: T;
	error?: string;
	balance?: number;
	required?: number;
}

interface GenerateScriptResponse {
	script?: string;
	text?: string;
	segments?: Array<{ text?: string }>;
	[k: string]: unknown;
}

/** Mode B — generate a narration script via Claude (Worker → FastAPI). */
export async function generateNarrationScript(opts: {
	mode: "A" | "B";
	theme?: string | null;
	tone?: string | null;
	durationSec?: number;
	titleHint?: string;
	jobId?: string;
}): Promise<NarrationResult<GenerateScriptResponse>> {
	try {
		const r = await fetch("/api/narration/generate", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				mode: opts.mode,
				theme: opts.theme ?? undefined,
				tone: opts.tone ?? undefined,
				duration_sec: opts.durationSec ?? 60,
				title_hint: opts.titleHint,
				job_id: opts.jobId,
			}),
		});
		const json = (await r.json().catch(() => ({}))) as GenerateScriptResponse & {
			error?: string;
			balance?: number;
			required?: number;
		};
		if (!r.ok) {
			return {
				ok: false,
				status: r.status,
				error: json.error ?? `generate_failed_${r.status}`,
				balance: json.balance,
				required: json.required,
			};
		}
		return { ok: true, status: r.status, data: json };
	} catch (e) {
		return { ok: false, status: 0, error: String(e) };
	}
}

/** Extract a plain-text script from the /generate response (shape-tolerant). */
export function scriptTextFromResponse(data: GenerateScriptResponse): string {
	if (typeof data.script === "string" && data.script.trim()) return data.script;
	if (typeof data.text === "string" && data.text.trim()) return data.text;
	if (Array.isArray(data.segments)) {
		return data.segments
			.map((s) => s?.text ?? "")
			.filter(Boolean)
			.join("\n");
	}
	return "";
}

/**
 * Synthesize TTS audio. On success returns an object-URL for the audio blob
 * (caller is responsible for revoking it).
 */
export async function synthesizeNarration(opts: {
	text: string;
	voiceId: string;
	tone?: string | null;
	language?: "ko" | "en" | "ja" | "zh";
	preferEngine?: "elevenlabs" | "edge_tts";
	speed?: number; // 0.5 .. 2.0 (Phase 25)
	pitch?: number; // -12 .. +12 semitones (Phase 25)
}): Promise<NarrationResult<{ audioUrl: string }>> {
	// D-2205 helper-first: try local Edge TTS via the user's Helper EXE
	// before the server route. Falls through to server on any
	// helper-unavailable / script-not-allowed code.
	try {
		const { helperSynthesizeNarration } = await import("./helper-engines");
		const helperRes = await helperSynthesizeNarration({
			text: opts.text,
			voiceId: opts.voiceId,
			language: opts.language,
			speed: opts.speed,
			pitch: opts.pitch,
			tone: opts.tone,
		});
		if (helperRes.ok && helperRes.downloadUrl) {
			return {
				ok: true,
				status: 200,
				data: { audioUrl: helperRes.downloadUrl },
			};
		}
		const fallbackCodes = new Set([
			"helper_unavailable",
			"helper_no_workspace",
			"PYTHON_SCRIPT_NOT_ALLOWED",
			"PYTHON_NOT_FOUND",
			"edge_tts_not_installed",
		]);
		const errCode = helperRes.error ?? "";
		if (
			!fallbackCodes.has(errCode) &&
			!fallbackCodes.has(errCode.toUpperCase()) &&
			!errCode.includes("PYTHON_SCRIPT_NOT_ALLOWED") &&
			!errCode.includes("edge_tts_not_installed")
		) {
			// Real Helper failure (not "Helper cannot do this") -> surface honestly.
			return {
				ok: false,
				status: 0,
				error: errCode || "helper_failed",
			};
		}
		// else fall through to server route
	} catch {
		// helper-engines import failed (very unlikely) -> fall through.
	}
	try {
		const r = await fetch("/api/narration/synthesize", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				text: opts.text,
				voice_id: opts.voiceId,
				tone: opts.tone ?? undefined,
				language: opts.language ?? "ko",
				prefer_engine: opts.preferEngine ?? "elevenlabs",
				speed: opts.speed ?? 1.0,
				pitch: opts.pitch ?? 0,
			}),
		});
		if (!r.ok) {
			const json = (await r.json().catch(() => ({}))) as {
				error?: string;
				balance?: number;
				required?: number;
			};
			return {
				ok: false,
				status: r.status,
				error: json.error ?? `synthesize_failed_${r.status}`,
				balance: json.balance,
				required: json.required,
			};
		}
		const blob = await r.blob();
		return {
			ok: true,
			status: r.status,
			data: { audioUrl: URL.createObjectURL(blob) },
		};
	} catch (e) {
		return { ok: false, status: 0, error: String(e) };
	}
}

/**
 * Upload a voice sample to clone (multipart). Before this the VoiceCloneWizard
 * faked training with a setInterval counter and never sent the file anywhere.
 */
export async function uploadVoiceClone(
	file: File,
	name?: string,
): Promise<NarrationResult<{ voiceId?: string }>> {
	try {
		const form = new FormData();
		form.append("file", file);
		if (name) form.append("name", name);
		const r = await fetch("/api/voice-clone/upload", {
			method: "POST",
			body: form,
		});
		const json = (await r.json().catch(() => ({}))) as {
			voice_id?: string;
			error?: string;
			balance?: number;
			required?: number;
		};
		if (!r.ok) {
			return {
				ok: false,
				status: r.status,
				error: json.error ?? `clone_upload_failed_${r.status}`,
				balance: json.balance,
				required: json.required,
			};
		}
		return { ok: true, status: r.status, data: { voiceId: json.voice_id } };
	} catch (e) {
		return { ok: false, status: 0, error: String(e) };
	}
}
